import React, { useState, useEffect } from "react";
import { io } from "socket.io-client";
import { useNavigate } from "react-router-dom";
import { LogoutButton } from "../Common/LogoutButton";
import "./Requests.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";

import { apiFetch } from "../../utils/api";
import Loader from "../Common/Loader";

const BASE_URL = process.env.REACT_APP_BASE_URL || "http://localhost:5000";

const formatDateTimeSplit = (dateString) => {
  if (!dateString) return { date: "—", time: "" };
  const dateObj = new Date(dateString);
  const date = dateObj.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  const time = dateObj.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return { date, time };
};

const Requests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [commentValue, setCommentValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterBy, setFilterBy] = useState("vehicle");
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [rejectedRequests, setRejectedRequests] = useState({});
  const [acceptedRequests, setAcceptedRequests] = useState({});
  const [breakdownFilter, setBreakdownFilter] = useState("");
  const [driverIdFilter, setDriverIdFilter] = useState("");
  const [blockedDrivers, setBlockedDrivers] = useState({});

  const [showCompleteConfirm, setShowCompleteConfirm] = useState(null);
  const [showRejectConfirm, setShowRejectConfirm] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [socket, setSocket] = useState(null);

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (!user) {
      navigate("/auth", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    // Initialize socket connection
    const newSocket = io(`${BASE_URL}`,{withCredentials: true});
    setSocket(newSocket);

    fetchRequests();
    fetchBlockedDrivers();
    const interval = setInterval(fetchRequests, 30000);

    newSocket.on("maintenanceRequestAdded", fetchRequests);
    newSocket.on("newRegistrationRequest", fetchRequests);
    newSocket.on("requestStatusUpdated", fetchRequests);

    // 🚨 REAL-TIME: Auto-refresh when new maintenance request is submitted
    newSocket.on("newMaintenanceRequest", (data) => {
      console.log(
        "🔄 New maintenance request received - auto-refreshing:",
        data
      );
      fetchRequests(); // Immediately refresh the requests list
    });

    return () => {
      clearInterval(interval);
      newSocket.disconnect();
    };
  }, []);

  const fetchBlockedDrivers = async () => {
    try {
      const response = await apiFetch("/api/drivers/blocked");

      const blockedData = {};
      response.forEach((driver) => {
        blockedData[driver.id] = true;
      });

      setBlockedDrivers(blockedData);
    } catch (error) {
      console.error("Failed to fetch blocked drivers:", error);
    }
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const [reqRes, driverRes] = await Promise.all([
        apiFetch("/api/requests/maintenanceReq", { method: "GET" }),
        apiFetch("/api/drivers/getDrivers", { method: "GET" }),
      ]);

      // console.log("Maintenance Requests from backend:", reqRes);

      const driverMap = {};
      driverRes.drivers.forEach((driver) => {
        driverMap[driver.operator_id] = driver.mobile;
      });

      const formatted = reqRes.map((req) => {
        const fallbackMobile = driverMap[req.DRIVER_ID] || "N/A";
        return {
          id: req.ID,
          driverId: req.DRIVER_ID,
          vehicleNumber: req.VEHICLE_TYPE || "N/A",
          name: req.DRIVER_NAME,
          mobileNumber: req.MOBILE_NUMBER || fallbackMobile,
          role: req.ROLE,
          status: req.STATUS,
          forklifts: 0,
          comment: req.COMMENT_TEXT,
          vehicleType: req.VEHICLE_TYPE,
          issue: req.ISSUE,
          requestDate: req.REQUEST_DATE,
          lastMaintenanceDate: req.LAST_MAINTENANCE_DATE || null,
          breakdownType: req.BREAKDOWN_TYPE || "",
        };
      });

      setRequests(formatted);
    } catch (error) {
      console.error("❌ Failed to fetch requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const validateField = (fieldName, value) => {
    const errors = { ...validationErrors };

    if (!value || value === "") {
      errors[fieldName] = `${
        fieldName.charAt(0).toUpperCase() + fieldName.slice(1)
      } is mandatory`;
    } else {
      delete errors[fieldName];
    }

    setValidationErrors(errors);
    return !errors[fieldName];
  };

  const validateAllFields = () => {
    const errors = {};

    // Validate based on current filter type
    if (statusFilter === "breakdown" && !breakdownFilter) {
      errors.breakdown = "Breakdown type selection is mandatory";
    }

    if (statusFilter === "driverId" && !driverIdFilter) {
      errors.driverId = "Driver ID input is mandatory";
    }

    if (statusFilter === "driverName" && !searchTerm) {
      errors.driverName = "Driver name input is mandatory";
    }

    if (statusFilter === "date" && (!startDate || !endDate)) {
      if (!startDate) errors.startDate = "Start date is mandatory";
      if (!endDate) errors.endDate = "End date is mandatory";
    }

    if (
      statusFilter &&
      statusFilter !== "breakdown" &&
      statusFilter !== "driverId" &&
      statusFilter !== "driverName" &&
      statusFilter !== "date" &&
      !statusFilter
    ) {
      errors.status = "Status selection is mandatory";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditComment = (request) => {
    setEditingCommentId(request.id);
    setCommentValue(request.comment || "");
  };

  const handleSaveComment = async (requestId) => {
    if (!commentValue.trim()) {
      toast.error("Comment cannot be empty");
      return;
    }

    // Optimistic UI update
    setRequests((prev) =>
      prev.map((req) =>
        req.id === requestId ? { ...req, comment: commentValue } : req
      )
    );
    setEditingCommentId(null);

    try {
      const data = await apiFetch(
        `/api/requests/maintenanceReq/${requestId}/comment`,
        {
          method: "PUT",
          body: JSON.stringify({ comment_text: commentValue }),
        }
      );

      console.log("✅ Comment updated successfully:", data.message);
    } catch (error) {
      console.error("❌ Error updating comment:", error.message);
      toast.error("Failed to update comment");
    }
  };

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      const maintenanceDate =
        newStatus === "completed" ? new Date().toISOString() : null;

      const targetRequest = requests.find((r) => r.id === requestId);

      // Optimistic UI update
      setRequests((prev) =>
        prev.map((req) =>
          req.id === requestId ? { ...req, status: newStatus } : req
        )
      );

      // Update maintenance request status
      const maintresp = await apiFetch(`/api/requests/${requestId}/status`, {
        method: "PUT",
        body: JSON.stringify({
          status: newStatus,
          maintenance_date: maintenanceDate,
        }),
      });

      // console.log("maint resp = ", maintresp);
      // console.log("new status = ", newStatus);

      if (newStatus === "completed") {
        // Unblock driver automatically
        const unblockResp = await apiFetch(`/api/drivers/toggle-access`, {
          method: "PUT",
          body: JSON.stringify({
            driverId: targetRequest.driverId,
            active: 1,
          }),
        });

        // console.log("unblock driver resp = ", unblockResp);

        setBlockedDrivers((prev) => {
          const copy = { ...prev };
          delete copy[targetRequest.driverId];
          return copy;
        });

        toast.success(
          "✅ Request completed and driver has been unblocked automatically."
        );
        localStorage.setItem("wasPartialBreakdown", "true");
      }
    } catch (error) {
      console.error("❌ Failed to update status:", error);
      fetchRequests(); // rollback fetch in case of error
    }
  };

  const handleCompleteRequest = (requestId) => {
    setShowCompleteConfirm(requestId);
  };

  const confirmCompleteRequest = async () => {
    if (showCompleteConfirm) {
      try {
        // First mark request as completed
        await handleStatusChange(showCompleteConfirm, "completed");

        // Find the driver ID for this request
        const request = requests.find((req) => req.id === showCompleteConfirm);
        if (request && (request.driverId || request.driver_id)) {
          const driverId = request.driverId || request.driver_id;

          // Unblock driver in database
          await apiFetch(`/api/requests/unblock-driver`, {
            method: "PUT",
            body: JSON.stringify({
              driverId: driverId,
              reason: "maintenance_completed",
            }),
          });

          // Emit socket event to unblock driver and notify completion
          if (socket) {
            socket.emit("maintenanceCompleted", {
              driverId: driverId,
              requestId: showCompleteConfirm,
              message:
                "Your maintenance has been completed. You can now resume operations.",
            });

            socket.emit("driverUnblocked", {
              driverId: driverId,
              reason: "maintenance_completed",
              message: "Your maintenance is completed.",
            });
          }
        }

        // Close the popup immediately after successful completion
        setShowCompleteConfirm(null);

        // Refresh the requests list to update UI
        fetchRequests();
      } catch (error) {
        console.error("❌ Failed to complete maintenance:", error);
        setShowCompleteConfirm(null); // Close popup even if there's an error
      }
    }
  };

  const confirmRejectRequest = async () => {
    if (showRejectConfirm && rejectReason.trim()) {
      try {
        // First update the comment with rejection reason
        await apiFetch(
          `/api/requests/maintenanceReq/${showRejectConfirm}/comment`,
          {
            method: "PUT",
            body: JSON.stringify({
              comment_text: `REJECTED: ${rejectReason.trim()}`,
            }),
          }
        );

        // Update status
        await handleStatusChange(showRejectConfirm, "rejected");

        // Find the driver ID for this request
        const request = requests.find((req) => req.id === showRejectConfirm);
        if (request && (request.driverId || request.driver_id)) {
          const driverId = request.driverId || request.driver_id;

          // Unblock driver in database
          await apiFetch(`/api/requests/unblock-driver`, {
            method: "PUT",
            body: JSON.stringify({
              driverId: driverId,
              reason: "maintenance_rejected",
            }),
          });

          // Emit socket event to unblock driver and notify rejection
          if (socket) {
            socket.emit("maintenanceRejected", {
              driverId: driverId,
              requestId: showRejectConfirm,
              reason: rejectReason.trim(),
              message: `Your maintenance request was rejected. Reason: ${rejectReason.trim()}`,
            });

            socket.emit("driverUnblocked", {
              driverId: driverId,
              reason: "maintenance_rejected",
              message: `Your maintenance was rejected because of the following reason: ${rejectReason.trim()}`,
            });
          }
        }

        // Close the popup immediately after successful rejection
        setShowRejectConfirm(null);
        setRejectReason("");

        // Refresh the requests list to update UI
        fetchRequests();
      } catch (error) {
        console.error("❌ Failed to reject maintenance:", error);

        // Close popup even if there's an error
        setShowRejectConfirm(null);
        setRejectReason("");
      }
    }
  };

  const handleSubmitFilters = () => {
    if (validateAllFields()) {
      // Clear validation errors on successful submission
      setValidationErrors({});
      toast.success("Filters applied successfully!");
    } else {
      toast.error("Please fill in all mandatory fields before submitting.");
    }
  };

  const clearAllFilters = () => {
    setStatusFilter("");
    setSearchTerm("");
    setBreakdownFilter("");
    setDriverIdFilter("");
    setStartDate(null);
    setEndDate(null);
    setValidationErrors({});
  };

  const hasActiveFilters = () => {
    return (
      statusFilter ||
      searchTerm ||
      breakdownFilter ||
      driverIdFilter ||
      startDate ||
      endDate
    );
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleString();

  const filteredRequests = requests.filter((req) => {
    let matchesSearch = true;
    if (statusFilter === "driverName") {
      matchesSearch = req.name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());
    } else {
      matchesSearch =
        req.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.vehicleNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.requestType?.toLowerCase().includes(searchTerm.toLowerCase());
    }

    let matchesStatus = true;

    if (statusFilter === "date") {
      const reqDate = req.requestDate ? new Date(req.requestDate) : null;
      matchesStatus =
        (!startDate && !endDate) ||
        (reqDate &&
          (!startDate || reqDate >= startDate) &&
          (!endDate || reqDate <= endDate));
    } else if (statusFilter === "breakdown") {
      matchesStatus = breakdownFilter
        ? req.breakdownType === breakdownFilter
        : true;
    } else if (statusFilter === "driverId") {
      matchesStatus = driverIdFilter
        ? req.driverId
            ?.toString()
            .toLowerCase()
            .includes(driverIdFilter.toLowerCase())
        : true;
    } else if (statusFilter === "driverName") {
      matchesStatus = true;
    } else {
      matchesStatus = statusFilter ? req.status === statusFilter : true;
    }

    return matchesSearch && matchesStatus;
  });

  const cancelEdit = () => setEditingCommentId(null);

  const handleRejectWithComment = (requestId) => {
    if (!commentValue.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }

    handleSaveComment(requestId);
    handleStatusChange(requestId, "rejected");
    setRejectedRequests((prev) => {
      const copy = { ...prev };
      delete copy[requestId];
      return copy;
    });
  };

  return (
    <div
      style={{ width: "100%", overflowX: "auto" }}
      className="requests-container"
    >
      <div className="requests-header">
        <div className="top-bar">
          <div className="left-controls">
            <button
              onClick={() => navigate("/maintenance-head")}
              className="back-button"
            >
              ← Back
            </button>
          </div>

          <div className="right-controls logout-wrapper">
            <LogoutButton />
          </div>
        </div>

        <h2 className="requests-heading">Maintenance Requests</h2>

        <div className="search-bar-wrapper">
          <div className="search-bar-wrapper">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                flexWrap: "wrap",
              }}
            >
              <select
                className={`filter-dropdown ${
                  validationErrors.status ? "error" : ""
                }`}
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  validateField("status", e.target.value);
                  // Reset related filters when switching filter types
                  if (e.target.value !== "breakdown") setBreakdownFilter("");
                  if (e.target.value !== "driverId") setDriverIdFilter("");
                  if (e.target.value !== "driverName") setSearchTerm("");
                  if (e.target.value !== "date") {
                    setStartDate(null);
                    setEndDate(null);
                  }
                }}
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="accepted">Accepted</option>
                <option value="completed">Completed</option>
                <option value="rejected">Rejected</option>
                <option value="date">Date</option>
                <option value="breakdown">Breakdown Type</option>
                <option value="driverId">Driver ID</option>
                <option value="driverName">Driver Name</option>
              </select>

              {hasActiveFilters() && (
                <button
                  onClick={clearAllFilters}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "#f56565",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                    fontWeight: "500",
                    transition: "background-color 0.2s",
                  }}
                  onMouseOver={(e) =>
                    (e.target.style.backgroundColor = "#e53e3e")
                  }
                  onMouseOut={(e) =>
                    (e.target.style.backgroundColor = "#f56565")
                  }
                >
                  Clear All Filters
                </button>
              )}

              {statusFilter && (
                <button
                  onClick={handleSubmitFilters}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "#4299e1",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                    fontWeight: "500",
                    transition: "background-color 0.2s",
                  }}
                  onMouseOver={(e) =>
                    (e.target.style.backgroundColor = "#3182ce")
                  }
                  onMouseOut={(e) =>
                    (e.target.style.backgroundColor = "#4299e1")
                  }
                >
                  Apply Filters
                </button>
              )}
            </div>

            {validationErrors.status && (
              <div className="validation-error">{validationErrors.status}</div>
            )}

            {statusFilter === "breakdown" && (
              <div style={{ marginTop: "10px" }}>
                <select
                  className={`filter-dropdown ${
                    validationErrors.breakdown ? "error" : ""
                  }`}
                  value={breakdownFilter}
                  onChange={(e) => {
                    setBreakdownFilter(e.target.value);
                    validateField("breakdown", e.target.value);
                  }}
                >
                  <option value="">Select Breakdown Type</option>
                  <option value="partial">Partial</option>
                  <option value="full">Full</option>
                </select>
                {validationErrors.breakdown && (
                  <div className="validation-error">
                    {validationErrors.breakdown}
                  </div>
                )}
              </div>
            )}

            {statusFilter === "driverId" && (
              <div style={{ marginTop: "10px" }}>
                <input
                  type="text"
                  className={`driver-id-filter-input ${
                    validationErrors.driverId ? "error" : ""
                  }`}
                  placeholder="Enter Driver ID to filter..."
                  value={driverIdFilter}
                  onChange={(e) => {
                    setDriverIdFilter(e.target.value);
                    validateField("driverId", e.target.value);
                  }}
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    width: "200px",
                    fontSize: "0.9rem",
                  }}
                />
                {validationErrors.driverId && (
                  <div className="validation-error">
                    {validationErrors.driverId}
                  </div>
                )}
              </div>
            )}

            {statusFilter === "driverName" && (
              <div style={{ marginTop: "10px" }}>
                <input
                  type="text"
                  className={`driver-id-filter-input ${
                    validationErrors.driverName ? "error" : ""
                  }`}
                  placeholder="Enter Driver Name to filter..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    validateField("driverName", e.target.value);
                  }}
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    width: "200px",
                    fontSize: "0.9rem",
                  }}
                />
                {validationErrors.driverName && (
                  <div className="validation-error">
                    {validationErrors.driverName}
                  </div>
                )}
              </div>
            )}
          </div>

          {statusFilter === "date" && (
            <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
              <div>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => {
                    setStartDate(date);
                    validateField("startDate", date);
                  }}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  placeholderText="Start Date"
                  isClearable
                  className={validationErrors.startDate ? "error" : ""}
                />
                {validationErrors.startDate && (
                  <div className="validation-error">
                    {validationErrors.startDate}
                  </div>
                )}
              </div>
              <div>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => {
                    setEndDate(date);
                    validateField("endDate", date);
                  }}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  placeholderText="End Date"
                  isClearable
                  className={validationErrors.endDate ? "error" : ""}
                />
                {validationErrors.endDate && (
                  <div className="validation-error">
                    {validationErrors.endDate}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="requests-status-summary">
        <div className="status-card pending">
          Pending: {requests.filter((r) => r.status === "pending").length}
        </div>
        <div className="status-card accepted">
          Accepted: {requests.filter((r) => r.status === "accepted").length}
        </div>
        <div className="status-card completed">
          Completed: {requests.filter((r) => r.status === "completed").length}
        </div>
        <div className="status-card rejected">
          Rejected: {requests.filter((r) => r.status === "rejected").length}
        </div>
      </div>

      {loading ? (
        <p>
          {" "}
          <Loader />{" "}
        </p>
      ) : (
        <table className="requests-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Driver ID</th>
              <th>Vehicle</th>
              <th>Driver Name</th>
              <th>Mobile</th>
              <th>Requested At</th>
              <th>Last Maintenance</th>
              <th>Status</th>
              <th>Comment</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRequests.map((req) => (
              <tr key={req.id}>
                <td>#{req.id}</td>
                <td className="driver-id-cell">
                  <span className="driver-id-badge">
                    {req.driverId || "N/A"}
                  </span>
                </td>
                <td>{req.vehicleNumber}</td>
                <td>{req.name}</td>
                <td>{req.mobileNumber}</td>

                <td>
                  {(() => {
                    const { date, time } = formatDateTimeSplit(req.requestDate);
                    return (
                      <>
                        <div>{date}</div>
                        <div style={{ fontSize: "0.8rem", color: "#555" }}>
                          {time}
                        </div>
                      </>
                    );
                  })()}
                </td>
                <td>
                  {(() => {
                    const { date, time } = formatDateTimeSplit(
                      req.lastMaintenanceDate
                    );
                    return (
                      <>
                        <div>{date}</div>
                        <div style={{ fontSize: "0.8rem", color: "#555" }}>
                          {time}
                        </div>
                      </>
                    );
                  })()}
                </td>

                <td>
                  <span className={`status-badge ${req.status}`}>
                    {req.status}
                  </span>
                </td>
                <td>
                  {editingCommentId === req.id ? (
                    <>
                      <textarea
                        className="comment-textarea"
                        rows="2"
                        value={commentValue}
                        onChange={(e) => setCommentValue(e.target.value)}
                      />
                      <div className="comment-edit-actions">
                        <button
                          className="apply-btn"
                          onClick={() => handleSaveComment(req.id)}
                        >
                          Save
                        </button>
                        <button className="cancel-btn" onClick={cancelEdit}>
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="comment-display">
                      <div className="comment-text">{req.comment || "—"}</div>

                      {/* Only show breakdown type if NOT in rejected state and NOT showing reject form */}
                      {!(
                        rejectedRequests[req.id] && req.status === "pending"
                      ) && (
                        <div
                          className="breakdown-type-badge"
                          style={{ marginTop: "6px" }}
                        >
                          {req.breakdownType === "full" && (
                            <span className="badge-full">Full Breakdown</span>
                          )}
                          {req.breakdownType === "partial" && (
                            <span className="badge-partial">
                              Partial Breakdown
                            </span>
                          )}
                          {!req.breakdownType && (
                            <span className="badge-none">Breakdown: N/A</span>
                          )}
                        </div>
                      )}

                      <div style={{ marginTop: "4px", textAlign: "right" }}>
                        <button
                          className="edit-btn"
                          onClick={() => handleEditComment(req)}
                        >
                          ✏️
                        </button>
                      </div>
                    </div>
                  )}
                </td>

                <td>
                  <div className="action-buttons">
                    {req.status === "pending" &&
                      !acceptedRequests[req.id] &&
                      !rejectedRequests[req.id] && (
                        <>
                          <button
                            className="accept-btn"
                            onClick={() => {
                              setAcceptedRequests((prev) => ({
                                ...prev,
                                [req.id]: true,
                              }));
                              handleStatusChange(req.id, "accepted");
                            }}
                          >
                            Accept
                          </button>
                          <button
                            className="reject-btn"
                            onClick={() => {
                              setShowRejectConfirm(req.id);
                              setRejectReason("");
                            }}
                          >
                            Reject
                          </button>
                        </>
                      )}

                    {acceptedRequests[req.id] && req.status === "pending" && (
                      <>
                        <button
                          className="complete-btn"
                          onClick={() => handleCompleteRequest(req.id)}
                        >
                          Complete
                        </button>
                        {blockedDrivers[req.driverId] && (
                          <span
                            style={{
                              color: "red",
                              fontWeight: "bold",
                              fontSize: "12px",
                              padding: "4px 8px",
                              backgroundColor: "#fee",
                              borderRadius: "4px",
                              border: "1px solid #fcc",
                            }}
                          >
                            🚫 Blocked
                          </span>
                        )}
                      </>
                    )}

                    {rejectedRequests[req.id] && req.status === "pending" && (
                      <div className="reject-comment-box">
                        <textarea
                          rows={2}
                          placeholder="State your reason for rejection..."
                          value={commentValue}
                          onChange={(e) => setCommentValue(e.target.value)}
                          className="comment-textarea"
                        />
                        <button
                          className="apply-btn"
                          onClick={() => handleRejectWithComment(req.id)}
                        >
                          Submit Rejection
                        </button>
                      </div>
                    )}

                    {req.status === "accepted" && (
                      <>
                        <button
                          className="complete-btn"
                          onClick={() => handleCompleteRequest(req.id)}
                        >
                          Mark Complete
                        </button>
                        {blockedDrivers[req.driverId] && (
                          <span
                            style={{
                              color: "red",
                              fontWeight: "bold",
                              fontSize: "12px",
                              padding: "4px 8px",
                              backgroundColor: "#fee",
                              borderRadius: "4px",
                              border: "1px solid #fcc",
                            }}
                          >
                            🚫 Blocked
                          </span>
                        )}
                      </>
                    )}

                    {req.status === "completed" && (
                      <span style={{ color: "green", fontWeight: "bold" }}>
                        ✅ Completed
                      </span>
                    )}

                    {req.status === "rejected" && (
                      <span style={{ color: "red", fontWeight: "bold" }}>
                        ❌ Rejected
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Complete Confirmation Popup */}
      {showCompleteConfirm && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "white",
              border: "2px solid #ccc",
              padding: "30px",
              borderRadius: "10px",
              textAlign: "center",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              minWidth: "350px",
            }}
          >
            <h3 style={{ marginBottom: "20px", color: "#333" }}>
              Complete Maintenance Confirmation
            </h3>
            <p style={{ marginBottom: "20px", fontSize: "16px" }}>
              Are you sure you want to complete this maintenance request?
            </p>
            <p
              style={{ marginBottom: "20px", fontSize: "14px", color: "#666" }}
            >
              This will mark the request as completed and unblock the driver.
            </p>
            <div
              style={{ display: "flex", justifyContent: "center", gap: "15px" }}
            >
              <button
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#48bb78",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                }}
                onClick={confirmCompleteRequest}
              >
                Yes, Complete
              </button>
              <button
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
                onClick={() => setShowCompleteConfirm(null)}
              >
                No, Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Confirmation Dialog */}
      {showRejectConfirm && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "white",
              border: "2px solid #dc3545",
              padding: "30px",
              borderRadius: "10px",
              textAlign: "center",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              minWidth: "400px",
              maxWidth: "500px",
            }}
          >
            <h3 style={{ marginBottom: "20px", color: "#dc3545" }}>
              Reject Maintenance Request
            </h3>
            <p style={{ marginBottom: "20px", fontSize: "16px" }}>
              Please provide a reason for rejecting this maintenance request:
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason..."
              style={{
                width: "100%",
                minHeight: "100px",
                padding: "10px",
                border: "2px solid #ddd",
                borderRadius: "5px",
                fontSize: "14px",
                marginBottom: "20px",
                resize: "vertical",
              }}
            />
            <div
              style={{ display: "flex", justifyContent: "center", gap: "15px" }}
            >
              <button
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: rejectReason.trim() ? "pointer" : "not-allowed",
                  fontSize: "14px",
                  fontWeight: "bold",
                  opacity: rejectReason.trim() ? 1 : 0.5,
                }}
                onClick={confirmRejectRequest}
                disabled={!rejectReason.trim()}
              >
                Submit Rejection
              </button>
              <button
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
                onClick={() => {
                  setShowRejectConfirm(null);
                  setRejectReason("");
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Requests;
